/**
 * Secure Token Storage Utility
 * 
 * Provides encrypted storage for authentication tokens in localStorage.
 * This mitigates XSS risks by encrypting sensitive data before storage.
 * 
 * Note: This is a client-side encryption layer. For maximum security,
 * tokens should be stored in httpOnly cookies (requires backend changes).
 * 
 * @module utils/tokenStorage
 */

import CryptoJS from 'crypto-js';

/**
 * Encryption key for token storage
 * In production, this should be set via environment variable
 * and should be unique per deployment
 */
const getEncryptionKey = () => {
  // Use environment variable if available, otherwise fallback
  // TODO: Set REACT_APP_TOKEN_ENCRYPTION_KEY in .env files
  const envKey = process.env.REACT_APP_TOKEN_ENCRYPTION_KEY;
  
  if (envKey) {
    return envKey;
  }
  
  // Fallback key - should be replaced with env var in production
  // This is a temporary measure and should be changed
  console.warn('Using fallback encryption key. Set REACT_APP_TOKEN_ENCRYPTION_KEY in .env');
  return 'riskcoach-token-encryption-key-change-in-production';
};

const ENCRYPTION_KEY = getEncryptionKey();

/**
 * Token storage keys
 */
export const TOKEN_KEYS = {
  AUTH_TOKEN: 'token',
  LINKGEN_TOKEN: 'linkgen_token'
};

/**
 * Encrypts and stores a token in localStorage
 * 
 * @param {string} key - Storage key name
 * @param {string} value - Token value to store
 * @throws {Error} If encryption fails
 * 
 * @example
 * import { setToken, TOKEN_KEYS } from '../utils/tokenStorage';
 * setToken(TOKEN_KEYS.AUTH_TOKEN, 'jwt-token-here');
 */
export const setToken = (key, value) => {
  if (!key || !value) {
    console.warn('setToken: key and value are required');
    return;
  }
  
  try {
    const encrypted = CryptoJS.AES.encrypt(value, ENCRYPTION_KEY).toString();
    localStorage.setItem(key, encrypted);
  } catch (error) {
    console.error('Failed to encrypt and store token:', error);
    throw new Error('Token storage failed');
  }
};

/**
 * Retrieves and decrypts a token from localStorage
 * 
 * @param {string} key - Storage key name
 * @returns {string|null} Decrypted token or null if not found/invalid
 * 
 * @example
 * import { getToken, TOKEN_KEYS } from '../utils/tokenStorage';
 * const token = getToken(TOKEN_KEYS.AUTH_TOKEN);
 */
export const getToken = (key) => {
  if (!key) {
    return null;
  }
  
  try {
    const encrypted = localStorage.getItem(key);
    
    if (!encrypted) {
      return null;
    }
    
    const decrypted = CryptoJS.AES.decrypt(encrypted, ENCRYPTION_KEY);
    const token = decrypted.toString(CryptoJS.enc.Utf8);
    
    // If decryption failed, token will be empty
    if (!token) {
      console.warn('Failed to decrypt token, may be corrupted or using different key');
      // Try to read as plain text (backward compatibility during migration)
      return localStorage.getItem(key);
    }
    
    return token;
  } catch (error) {
    console.error('Failed to decrypt token:', error);
    // Fallback: try reading as plain text (for migration period)
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
};

/**
 * Removes a token from localStorage
 * 
 * @param {string} key - Storage key name
 * 
 * @example
 * import { removeToken, TOKEN_KEYS } from '../utils/tokenStorage';
 * removeToken(TOKEN_KEYS.AUTH_TOKEN);
 */
export const removeToken = (key) => {
  if (!key) {
    return;
  }
  
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to remove token:', error);
  }
};

/**
 * Removes all authentication tokens
 * Useful for logout functionality
 * 
 * @example
 * import { clearAllTokens } from '../utils/tokenStorage';
 * clearAllTokens();
 */
export const clearAllTokens = () => {
  Object.values(TOKEN_KEYS).forEach((key) => {
    removeToken(key);
  });
};

/**
 * Checks if a token exists in storage
 * 
 * @param {string} key - Storage key name
 * @returns {boolean} True if token exists
 */
export const hasToken = (key) => {
  if (!key) {
    return false;
  }
  
  return localStorage.getItem(key) !== null;
};

/**
 * Migrates existing plain-text tokens to encrypted format
 * Call this once during app initialization to migrate existing tokens
 * 
 * @example
 * import { migrateTokens } from '../utils/tokenStorage';
 * migrateTokens();
 */
export const migrateTokens = () => {
  Object.values(TOKEN_KEYS).forEach((key) => {
    const existingValue = localStorage.getItem(key);
    
    if (existingValue) {
      try {
        // Try to decrypt - if it fails, it's plain text and needs migration
        const decrypted = CryptoJS.AES.decrypt(existingValue, ENCRYPTION_KEY);
        const token = decrypted.toString(CryptoJS.enc.Utf8);
        
        // If decryption produces empty string, it's likely plain text
        if (!token && existingValue.length > 0) {
          // Re-encrypt the plain text token
          setToken(key, existingValue);
          console.log(`Migrated token: ${key}`);
        }
      } catch {
        // If decryption throws error, assume it's plain text
        setToken(key, existingValue);
        console.log(`Migrated token: ${key}`);
      }
    }
  });
};

